// Maps game events to sounds (plan 18): subscribes to the `EventBus` and
// drives the engine — the audio half of what `Cube.cs` does view-side. The
// sim never changes: the director only reads (00-architecture.md §4), through
// the same narrow views the renderers use (it needs `BugKind` by id for the
// death sounds).

import { SFX_BANKS, MUSIC, type SfxBankName, type MusicTrack } from "./banks";
import type { EventBus } from "../game/events";
import type { BugId, BugKind } from "../game/bugs";
import type { DifficultyDef } from "../game/defs";
import type { Rng } from "../game/rng";

/**
 * What the director calls — `AudioEngine` implements it structurally; tests
 * inject a recorder.
 */
export interface AudioSink {
  playSfx(url: string, delaySeconds?: number): void;
  playMusic(track: MusicTrack): void;
}

/** The slice of `World` the director reads. */
export interface DirectorWorld {
  readonly events: EventBus;
  readonly bugs: {
    readonly bugs: ReadonlyMap<BugId, { readonly kindDef: BugKind }>;
  };
}

/**
 * DECIDED (plan 18's spawn-sound fossil, option a — port the intent): the
 * original sets a spawn clip on every new bug and calls `PlayDelayed(11025)`
 * (`Cube.cs:1832,1837`); Unity 5's `PlayDelayed` takes **seconds**, so the
 * shipped game schedules every spawn sound ~3 h out and none is ever heard.
 * 11025 is a fossil of the deprecated `Play(delay)` that took 44.1 kHz
 * *samples*, where 11025 = 0.25 s — the sounds were authored, bound, and
 * broken by an API migration (precedent: plan 12 chose intended over shipped
 * defect). See DIVERGENCES.md → Corrections.
 */
export const SPAWN_SFX_DELAY_SECONDS = 0.25;

/** One decided play, for `buggsDebug.audioLog()`. */
export interface AudioLogEntry {
  /** Seconds since page load (the injected clock). */
  readonly t: number;
  /** The bank name, or `music:<track>`. */
  readonly kind: string;
  /** The chosen clip's file name. */
  readonly clip: string;
}

const LOG_CAPACITY = 256;

function clipName(url: string): string {
  const last = url.split("/").pop() ?? url;
  return last.split("?")[0];
}

export class AudioDirector {
  private readonly log: AudioLogEntry[] = [];
  private unsubscribes: (() => void)[] = [];
  private world: DirectorWorld | null = null;
  private inGame = false;
  /** Crossing into this level switches to the second-half music. */
  private halfLevel = 0;
  /** Set once `gameWon`/`gameLost` fired: music stops reacting (the original
   * freezes the whole game there; the port's sim keeps running until shell
   * part 2, so only the music side goes quiet — SFX keep narrating). */
  private ended = false;

  constructor(
    private readonly sink: AudioSink,
    // Same seeded-RNG family as the sim but a separate stream: sound choice
    // must not perturb gameplay draws (`main.ts` salts the seed).
    private readonly rng: Rng,
    private readonly now: () => number,
  ) {}

  /** The menu scene: Summer once, demo-game SFX, silent blocks. */
  attachMenu(world: DirectorWorld): void {
    this.attach(world, false);
    this.playMusic(MUSIC.menu);
  }

  /** A run at `difficulty`: Balloon_6 looped, everything audible. */
  attachGame(world: DirectorWorld, difficulty: DifficultyDef): void {
    this.attach(world, true);
    // C# integer division (`gameWonAfterLevel / 2`, `Cube.cs:3241`).
    this.halfLevel = Math.floor(difficulty.winAfterLevel / 2);
    this.playMusic(MUSIC.inGame);
  }

  /** Every quarter-turn start — game swipe or menu page turn (`Cube.cs:3387`,
   * `:3478-3536`; the menu binds the same bank). Called by `main.ts`'s one
   * turn-starting helper, which both paths feed. */
  cubeRotateStarted(): void {
    this.playBank("CubeRotates");
  }

  audioLog(): readonly AudioLogEntry[] {
    return this.log;
  }

  private attach(world: DirectorWorld, inGame: boolean): void {
    for (const unsubscribe of this.unsubscribes.splice(0)) unsubscribe();
    this.world = world;
    this.inGame = inGame;
    this.ended = false;
    const events = world.events;
    this.unsubscribes = [
      events.on("bugSpawned", ({ kind }) => {
        this.playBank(kind === "black" ? "BlackBuggSpawn" : "BugSpawn", SPAWN_SFX_DELAY_SECONDS);
      }),
      events.on("bugStateChanged", ({ id, state }) => this.bugStateSound(id, state)),
      events.on("bugHit", () => this.playBank("FatBugHit")),
      events.on("blockChanged", ({ state }) => {
        // Only while a real run is on — the original wraps exactly these two
        // one-shots in `gameStates == Running` (`Cube.cs:1943`, `:1958`),
        // which is what keeps the menu's decaying demo cube quiet (its scene
        // nulls the block banks too). `ghost`/`empty` play nothing: the
        // eat-through bank (`SubBlockBuildsDown`) is empty in the scene.
        if (!this.inGame) return;
        if (state === "infected") this.playBank("BlockBuildsDown");
        else if (state === "healthy") this.playBank("BlockBuildsUp");
      }),
      events.on("combo", ({ length }) => {
        // Lengths 2-5, as `Cube.cs:2135`; every reachable index of the
        // original's `DiamondsPick` list is the same clip (see banks.ts).
        if (length >= 2 && length <= 5) this.playBank("DiamondsPick");
      }),
      events.on("powerUpSpawned", () => this.playBank("PowerUpSpawns")),
      events.on("powerUpStateChanged", ({ state }) => {
        if (state === "taken") this.playBank("PowerUpTakenByPlayer");
        else if (state === "killed") this.playBank("PowerUpKilledByBug");
      }),
      // `spawnerRemoved` and the apple cull play `SpawnPointDies` in the
      // original — an empty bank, silent by design; `repairBlocked` never
      // reaches a sound at all. Neither is subscribed.
      events.on("levelChanged", ({ level }) => {
        if (!this.inGame || this.ended) return;
        // The stinger overlays channel 1 at every crossing, including the
        // level-1 `LetsGo` and the final-level billboard (`Cube.cs:3275-3295`).
        this.playMusic(MUSIC.levelUp);
        if (level === this.halfLevel) this.playMusic(MUSIC.inGameSecondHalf);
      }),
      events.on("gameWon", () => {
        if (!this.inGame || this.ended) return;
        this.ended = true;
        // APPROX: the original picks gold/silver trophy music by comparing the
        // score to the online highscore (`Cube.cs:2311`); no highscore service
        // exists here, so a win always takes gold (SUCCESS.mp3).
        this.playMusic(MUSIC.goldTrophy);
      }),
      events.on("gameLost", () => {
        if (!this.inGame || this.ended) return;
        this.ended = true;
        // `ConductBugsGameOver` one-shots BlockBuildsDown on top of the
        // game-over music (`Cube.cs:2277`).
        this.playMusic(MUSIC.gameOver);
        this.playBank("BlockBuildsDown");
      }),
    ];
  }

  /** The death/pickup switch of `_bugsModel_BugStateChanged` (`Cube.cs:1506`). */
  private bugStateSound(id: BugId, state: string): void {
    if (state === "infecting") {
      this.playBank("BugInfects");
      return;
    }
    const kind = this.world?.bugs.bugs.get(id)?.kindDef ?? null;
    if (state === "dying") {
      // The black branch covers both its 20 s despawn and (for now) the
      // player tap — the tap-kill distinction arrives with the `TODO(black)`
      // instant game over, whose port-side state is still `dying`.
      if (kind === "fat") this.playBank("FatBugDies");
      else if (kind === "black") this.playBank("BlackBuggDisappearsByItself");
      else this.playBank("BugDies");
      return;
    }
    if (state === "takenByPlayer") {
      // Only the lady reaches this state today; the full original switch is
      // ported so the mapping stays right if that changes (`Cube.cs:1561-1577`).
      if (kind === "fat") this.playBank("FatBugDies");
      else if (kind === "black") this.playBank("BlackBuggPickedByPlayer");
      else if (kind === "lady") this.playBank("LadyBugTakenByPlayer");
      else this.playBank("BugDies");
    }
  }

  /** Uniform pick from a bank (`Cube.GetAnyOf`); empty banks are silent. */
  private playBank(bank: SfxBankName, delaySeconds = 0): void {
    const clips = SFX_BANKS[bank];
    if (clips.length === 0) return;
    const url = clips[Math.floor(this.rng() * clips.length)];
    this.record(bank, clipName(url));
    this.sink.playSfx(url, delaySeconds);
  }

  private playMusic(track: MusicTrack): void {
    this.record(`music:${track.name}`, clipName(track.url));
    this.sink.playMusic(track);
  }

  /** Ring buffer of decisions — written even while the engine is locked. */
  private record(kind: string, clip: string): void {
    this.log.push({ t: this.now(), kind, clip });
    if (this.log.length > LOG_CAPACITY) this.log.splice(0, this.log.length - LOG_CAPACITY);
  }
}
